import React, { useState } from 'react';

/**
 * ChatChoice — renders a choice prompt with clickable option buttons.
 * Shows a maximum of `pageSize` options at a time with paging controls
 * if there are more options than the page size.
 *
 * @param {Object} props
 * @param {string} props.choiceId - Unique ID for this choice
 * @param {string} props.question - The question to display
 * @param {Array<{id: string, label: string}>} props.options - Available options
 * @param {number} props.pageSize - Max options visible at once (default 4)
 * @param {function(string): void} props.onSelect - Called with the selected option ID
 */
export default function ChatChoice({ choiceId, question, options, pageSize = 4, onSelect }) {
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedId, setSelectedId] = useState(null);

    const hasOptions = options && options.length > 0;
    const totalPages = Math.ceil((options?.length || 0) / pageSize);
    const startIndex = currentPage * pageSize;
    const visibleOptions = hasOptions ? options.slice(startIndex, startIndex + pageSize) : [];

    const handleSelect = (optionId) => {
        setSelectedId(optionId);
        onSelect(optionId);
    };

    const handlePrevPage = () => {
        setCurrentPage((prev) => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
    };

    return (
        <div className="chat-choice-container">
            <div className="chat-choice-question">{question}</div>
            {hasOptions ? (
                <>
                    <div className="chat-choice-options">
                        {visibleOptions.map((option) => (
                            <button
                                key={option.id}
                                className={`chat-choice-option${selectedId === option.id ? ' chat-choice-option-selected' : ''}`}
                                onClick={() => handleSelect(option.id)}
                                disabled={selectedId !== null}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <div className="chat-choice-paging">
                            <button
                                className="chat-choice-page-btn"
                                onClick={handlePrevPage}
                                disabled={currentPage === 0 || selectedId !== null}
                            >
                                ← Prev
                            </button>
                            <span className="chat-choice-page-info">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                                className="chat-choice-page-btn"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages - 1 || selectedId !== null}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="chat-choice-no-options">
                    Type your response below
                </div>
            )}
            {selectedId && (
                <div className="chat-choice-selected">
                    ✓ Selection sent — continuing…
                </div>
            )}
            {!selectedId && hasOptions && (
                <div className="chat-choice-hint">
                    Click an option above, or type your own response below
                </div>
            )}
        </div>
    );
}
